import React, { useCallback, useEffect, useState, useRef } from 'react';
import { withTranslation, Trans } from 'react-i18next';
import { useAPIError, useUser } from '../../context';

import {
    InternalGrid,
    ModalProvider,
    ThemeDefault,
    Text,
    Box,
    TrashIcon,
    CoinsIcon,
    DaButton,
    ButtonGroup,
    ModalControl,
    HeartIcon,
    SkeletonItem,
} from '@paygreen/paygreen-ui';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';

import {
    H1Title,
    ProjectCard,
    ErrorBoundary,
    SmallDataCard,
} from '../../components';
import { IsDesktop } from '../../utils/tools';
import { useNodeDimensions } from '../../utils/hooks';
import { getUserProjects, replaceFavorite } from './service';
import ProjectCardSkeleton from './ProjectCardSkeleton';
import EmptyProject from './EmptyProject';

const MyProjects = ({ t }) => {
    const renderCardConfig = ({ isActive }) => {
        const cardConfig = {
            colorWab: isActive === '0' ? 'white20' : null,
            colorPallet: isActive === '0' ? 'wab' : null,
            isShadowWab: isActive === '0' ? false : true,
            blockWidth: 'md',
        };

        return cardConfig;
    };

    const { addError } = useAPIError();
    const maxProject = 6;
    const [isLoading, setIsLoading] = useState(true);

    const [projects, setProjects] = useState([]);
    const [emptyProject, setEmptyProject] = useState([]);

    const { user } = useUser();

    // we create a ref to detect if component is mounted
    const isMounted = useRef(false);
    const isDesktop = IsDesktop();

    // we create a ref using a special hook to get width from InternalGrid component
    const [ref, { width }] = useNodeDimensions(isMounted.current);

    const fetchData = useCallback(() => {
        getUserProjects(user?.idUser?.toString())
            .then((res) => {
                setProjects(res.data._embedded.public_carbon_project);
                setIsLoading(false);
            })
            .catch((error) => {
                addError(
                    error?.response?.data.detail,
                    error?.response?.data.status,
                );
                setIsLoading(false);
            });
    }, [user, addError]);

    const handleReplaceFavorite = async (idProject, name) => {
        try {
            setIsLoading(true);

            await replaceFavorite(idProject);

            fetchData();
        } catch (error) {
            addError(
                error?.response?.data.detail,
                error?.response?.data.status,
            );
        }

        toast(
            <Text colorPallet="theme" textSize="xs">
                <Trans
                    i18nKey="contributions.globalView.globalCard.addProjectMessage"
                    values={{ project: name }}
                    components={{ bold: <strong /> }}
                >
                    Le projet <bold>{{ name }}</bold> est maintenant votre
                    projet favori 🎉
                </Trans>
            </Text>,
        );

        setIsLoading(false);
    };

    const renderCardContent = (
        { isDefault, isActive, idProject, name },
        userContribution,
        clientContribution,
    ) => {
        return (
            <InternalGrid
                style={{
                    flexDirection: 'column',
                }}
                childrenFlex={1}
                justifyContent="center"
                alignItems="stretch"
            >
                <InternalGrid
                    displayType="grid"
                    gridTemplateColumns="1fr 1fr"
                    alignItems="center"
                    justifyItems="stretch"
                    childrenMarginTop="xs"
                    hasStaticWidth
                    childrenMarginLateral="sm"
                >
                    <ErrorBoundary>
                        <SkeletonItem
                            colorWab="grey10"
                            radiusSize="sm"
                            isLoading={!userContribution}
                        >
                            <SmallDataCard
                                radiusSize="sm"
                                isDisabled={isActive === '0'}
                                blockWidth="lg"
                                colorPallet="theme"
                                colorTheme="secondary"
                                title={t(
                                    'contributions.globalView.globalCard.yourCustomerPaid',
                                )}
                                icon={<CoinsIcon />}
                                unit="€"
                                value={userContribution || 0}
                            />
                        </SkeletonItem>

                        <SkeletonItem
                            colorWab="grey10"
                            radiusSize="sm"
                            isLoading={!clientContribution}
                        >
                            <SmallDataCard
                                radiusSize="sm"
                                isDisabled={isActive === '0'}
                                blockWidth="lg"
                                colorPallet="theme"
                                colorTheme="primary"
                                title={t(
                                    'contributions.globalView.globalCard.youPaid',
                                )}
                                icon={<CoinsIcon />}
                                unit="€"
                                value={clientContribution}
                            />
                        </SkeletonItem>
                    </ErrorBoundary>
                </InternalGrid>

                <Box>
                    <InternalGrid
                        displayType="grid"
                        gridTemplateColumns="1fr 1fr"
                        justifyItems="center"
                        alignItems="center"
                        style={{ width: '100%' }}
                    >
                        <ButtonGroup marginBottom="xs" marginTop="xs">
                            <ModalControl
                                hasOnClickControl={!!Number(isDefault)}
                            >
                                <button
                                    disabled={!!Number(isDefault)}
                                    type="button"
                                >
                                    <DaButton
                                        isDisabled={!!Number(isDefault)}
                                        buttonSize="md"
                                        buttonStyle="line"
                                        gradient="none"
                                        colorType="original"
                                        colorPallet={
                                            !!Number(isDefault)
                                                ? 'theme'
                                                : 'status'
                                        }
                                        colorStatus="danger"
                                        title={
                                            !!Number(isDefault)
                                                ? t(
                                                      'contributions.globalView.globalCard.removeProject',
                                                      { projet: name },
                                                  )
                                                : null
                                        }
                                        icon={<TrashIcon />}
                                    >
                                        {t(
                                            'contributions.carbonProjects.smallDataCard.remove',
                                        )}
                                    </DaButton>
                                </button>
                            </ModalControl>
                        </ButtonGroup>

                        <ButtonGroup marginBottom="xs" marginTop="xs">
                            {renderIcon(isDefault, isActive, idProject, name)}
                        </ButtonGroup>
                    </InternalGrid>
                </Box>
            </InternalGrid>
        );
    };

    useEffect(() => {
        isMounted.current = true;
        const renderEmptyCard = () => {
            const emptyProjectCard = Array.from(
                Array(maxProject - projects.length),
            );

            setEmptyProject(emptyProjectCard);
        };

        if (isMounted.current && projects.length === 0) {
            fetchData();
        }
        renderEmptyCard();

        return () => {
            isMounted.current = false;
        };
    }, [fetchData, projects]);

    const renderIcon = (isDefault, isActive, idProject, name) => {
        if (Number(isActive) === 0) {
            return (
                <InternalGrid alignItems="center">
                    <Text htmlTag="strong" textSize="xs" colorPallet="theme">
                        {t('contributions.globalView.globalCard.fundedProject')}
                    </Text>
                </InternalGrid>
            );
        }
        if (isDefault === '1') {
            return (
                <InternalGrid alignItems="center">
                    <button disabled type="button">
                        <DaButton
                            buttonSize="md"
                            colorType="reverse"
                            colorPallet="theme"
                            colorTheme="primary"
                            icon={<HeartIcon />}
                        >
                            <Text colorPallet="theme" textSize="xs">
                                {t(
                                    'contributions.carbonProjects.smallDataCard.favoriteProject',
                                )}
                            </Text>
                        </DaButton>
                    </button>
                </InternalGrid>
            );
        }
        return (
            <InternalGrid alignItems="center">
                <button type="button">
                    <DaButton
                        onClick={() => handleReplaceFavorite(idProject, name)}
                        buttonSize="md"
                        colorPallet="theme"
                        colorTheme="primary"
                        icon={<HeartIcon />}
                    >
                        <Text
                            colorPallet="wab"
                            colorWab="white00"
                            textSize="xs"
                        >
                            {t(
                                'contributions.carbonProjects.smallDataCard.favoriteProject',
                            )}
                        </Text>
                    </DaButton>
                </button>
            </InternalGrid>
        );
    };

    return (
        <>
            <InternalGrid
                displayType="grid"
                alignItems="flex-start"
                justifyItems="start"
                hasStaticWidth={false}
                gridTemplateColumns="auto"
                gridGap="xs"
            >
                <Helmet>
                    <title>
                        {t('contributions.globalView.myCarbonProject')} -{' '}
                        {t('main.metaTitle')}
                    </title>
                </Helmet>

                <H1Title
                    marginTop={isDesktop ? 'none' : 'xs'}
                    title={t('contributions.globalView.myCarbonProject')}
                />
            </InternalGrid>

            <div ref={ref}>
                <InternalGrid
                    style={{
                        paddingTop: 20,
                    }}
                    displayType="grid"
                    gridGap="lg"
                    justifyItems="stretch"
                    alignItems="stretch"
                    childrenMarginTop="xs"
                    childrenMarginBottom="xs"
                    gridTemplateColumns={
                        width > ThemeDefault.breakpoint.lg
                            ? 'repeat(3, 1fr)'
                            : width > ThemeDefault.breakpoint.md
                            ? 'repeat(2, 1fr)'
                            : '1fr'
                    }
                >
                    {isLoading ? (
                        <ProjectCardSkeleton repeat={3} lineNumber={7} />
                    ) : (
                        <ErrorBoundary>
                            {projects?.map((project, index) => (
                                <ModalProvider key={index}>
                                    <ProjectCard
                                        userProjects
                                        projects={projects}
                                        project={project}
                                        setProjects={setProjects}
                                        fetchData={fetchData}
                                        blockWidth="md"
                                        isLoading={isLoading}
                                        setIsLoading={setIsLoading}
                                        handleReplaceFavorite={
                                            handleReplaceFavorite
                                        }
                                        renderCardContent={renderCardContent}
                                        renderCardConfig={renderCardConfig}
                                    />
                                </ModalProvider>
                            ))}

                            {emptyProject?.map((_, index) => (
                                <EmptyProject key={index} />
                            ))}
                        </ErrorBoundary>
                    )}
                </InternalGrid>
            </div>
        </>
    );
};

export default withTranslation('common')(MyProjects);
